import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";

import type { NoticeItem, NoticeTarget, NoticeUpsertPayload } from "src/types/notice/noticeTypes";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  item?: NoticeItem | null;
  loading?: boolean;

  onClose: () => void;
  onSubmit: (payload: NoticeUpsertPayload, file?: File | null) => Promise<boolean> | boolean;
};

const empty: NoticeUpsertPayload = {
  noticeTarget: "STORE",
  noticeTtl: "",
  noticeCts: "",
  noticeOrder: 1,
  pinnedYn: false,
  useYn: true,
  activeYn: true,
  activeStrDatetime: new Date().toISOString(),
  activeEndDatetime: new Date().toISOString(),
};

function toLocal(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}
function localToIso(v: string) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

export default function NoticeUpsertModal({ open, mode, item, loading, onClose, onSubmit }: Props) {
  const [payload, setPayload] = useState<NoticeUpsertPayload>(empty);
  const [file, setFile] = useState<File | null>(null);

  const [startLocal, setStartLocal] = useState("");
  const [endLocal, setEndLocal] = useState("");

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && item) {
      setPayload({
        noticeTarget: item.noticeTarget,
        noticeTtl: item.noticeTtl,
        noticeCts: item.noticeCts,
        noticeOrder: item.noticeOrder,
        pinnedYn: !!item.pinnedYn,
        useYn: !!item.useYn,
        activeYn: !!item.activeYn,
        activeStrDatetime: item.activeStrDatetime,
        activeEndDatetime: item.activeEndDatetime,
      });
      setStartLocal(toLocal(item.activeStrDatetime));
      setEndLocal(toLocal(item.activeEndDatetime));
      setFile(null);
    } else {
      setPayload(empty);
      setStartLocal(toLocal(empty.activeStrDatetime));
      setEndLocal(toLocal(empty.activeEndDatetime));
      setFile(null);
    }
  }, [open, mode, item]);

  useEffect(() => {
    setPayload((p) => ({ ...p, activeStrDatetime: localToIso(startLocal) }));
  }, [startLocal]);

  useEffect(() => {
    setPayload((p) => ({ ...p, activeEndDatetime: localToIso(endLocal) }));
  }, [endLocal]);

  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return item?.noticeFileUrl || "";
  }, [file, item]);

  const set = <K extends keyof NoticeUpsertPayload>(k: K, v: NoticeUpsertPayload[K]) =>
    setPayload((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!payload.noticeTtl.trim()) return alert("제목을 입력해주세요.");
    const ok = await onSubmit(payload, file);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === "create" ? "공지 등록" : "공지 수정"}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            select
            label="Target"
            value={payload.noticeTarget}
            onChange={(e) => {
              set("noticeTarget", e.target.value as NoticeTarget);
            }}
          >
            <MenuItem value="STORE">STORE</MenuItem>
            <MenuItem value="SYSTEM">SYSTEM</MenuItem>
            <MenuItem value="CLINIC">CLINIC</MenuItem>
          </TextField>

          <TextField
            label="제목"
            value={payload.noticeTtl}
            onChange={(e) => set("noticeTtl", e.target.value)}
          />

          <TextField
            label="내용"
            value={payload.noticeCts}
            onChange={(e) => set("noticeCts", e.target.value)}
            multiline
            minRows={6}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              type="number"
              label="정렬(NoticeOrder)"
              value={payload.noticeOrder}
              onChange={(e) => set("noticeOrder", Number(e.target.value))}
              sx={{ width: 220 }}
            />
            <FormControlLabel
              control={
                <Switch checked={payload.pinnedYn} onChange={(_, v) => set("pinnedYn", v)} />
              }
              label="상단고정(PIN)"
            />
            <FormControlLabel
              control={
                <Switch checked={payload.activeYn} onChange={(_, v) => set("activeYn", v)} />
              }
              label="활성"
            />
            <FormControlLabel
              control={<Switch checked={payload.useYn} onChange={(_, v) => set("useYn", v)} />}
              label="사용"
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              type="datetime-local"
              label="시작"
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 280 }}
            />
            <TextField
              type="datetime-local"
              label="종료"
              value={endLocal}
              onChange={(e) => setEndLocal(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 280 }}
            />
          </Stack>

          <Stack spacing={1}>
            <Button variant="outlined" component="label">
              파일 선택(이미지)
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>

            {previewUrl ? (
              <img
                src={previewUrl}
                alt="preview"
                style={{ width: 240, borderRadius: 12, border: "1px solid #eee" }}
              />
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={!!loading}>
          취소
        </Button>
        <Button variant="contained" onClick={submit} disabled={!!loading}>
          {loading ? "처리중..." : mode === "create" ? "등록" : "수정"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
